import React, { MouseEvent, useState, useEffect } from 'react';
import { Card, Flex, Button, Select, TextField, Text } from '@radix-ui/themes';

import {
  TransactionFingerprint,
  FingerprintValue,
  ConditionOperator,
  FingerprintValueType,
  TransactionField,
  SlotDataType,
  ListItemType,
} from '../../types';

import { MonetaryValueInput } from '@/components';
import {
  getOperationsForValueType,
  filterNullOperations,
  formatOperator,
  isListOperation,
  getListItemTypeForSlot,
} from './operationsByValueType';
import ListSelector from './ListSelector';

// Transaction field attributes with their types
export const transactionAttributes: {
  key: TransactionField;
  label: string;
  type: SlotDataType;
  valueType: FingerprintValueType;
  nullable: boolean;
}[] = [
  {
    key: TransactionField.From,
    label: 'From',
    type: SlotDataType.Address,
    valueType: FingerprintValueType.String,
    nullable: false, // Always present
  },
  {
    key: TransactionField.To,
    label: 'To',
    type: SlotDataType.Address,
    valueType: FingerprintValueType.String,
    nullable: true, // Null for contract creation transactions
  },
  {
    key: TransactionField.Value,
    label: 'Value',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.MonetaryValue,
    nullable: false, // Always present (can be 0)
  },
  {
    key: TransactionField.Gas,
    label: 'Gas',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: false, // Always present
  },
  {
    key: TransactionField.GasPrice,
    label: 'Gas Price',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: true, // May be null in EIP-1559 transactions
  },
  {
    key: TransactionField.MaxPriorityFeePerGas,
    label: 'Max Priority Fee Per Gas',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: true, // Only present in EIP-1559 transactions
  },
  {
    key: TransactionField.MaxFeePerGas,
    label: 'Max Fee Per Gas',
    type: SlotDataType.Number,
    valueType: FingerprintValueType.Number,
    nullable: true, // Only present in EIP-1559 transactions
  },
  {
    key: TransactionField.Input,
    label: 'Input',
    type: SlotDataType.Bytes,
    valueType: FingerprintValueType.String,
    nullable: false, // Always present (can be empty "0x")
  },
];

const TextFieldInput = ({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) => {
  return (
    <TextField.Root value={value} onChange={(e) => onChange(e.target.value)} />
  );
};

// Range input for Between/NotBetween with regular numbers
const NumberRangeInput = ({
  value,
  onChange,
}: {
  value: { min: string; max: string };
  onChange: (value: { min: string; max: string }) => void;
}) => {
  const rangeValue = value || { min: '', max: '' };
  return (
    <Flex direction="row" gap="2" align="center">
      <TextField.Root
        type="number"
        placeholder="Min"
        value={rangeValue.min}
        onChange={(e) => onChange({ ...rangeValue, min: e.target.value })}
        style={{ flex: 1 }}
      />
      <Text size="2" color="gray">
        and
      </Text>
      <TextField.Root
        type="number"
        placeholder="Max"
        value={rangeValue.max}
        onChange={(e) => onChange({ ...rangeValue, max: e.target.value })}
        style={{ flex: 1 }}
      />
    </Flex>
  );
};

// Range input for Between/NotBetween with monetary values
const MonetaryRangeInput = ({
  value,
  onChange,
}: {
  value: {
    min: { amount: number | string; currency: string };
    max: { amount: number | string; currency: string };
  };
  onChange: (value: {
    min: { amount: number | string; currency: string };
    max: { amount: number | string; currency: string };
  }) => void;
}) => {
  const rangeValue = value || {
    min: { amount: '', currency: 'ETH' },
    max: { amount: '', currency: 'ETH' },
  };
  return (
    <Flex direction="column" gap="2">
      <Flex direction="row" gap="2" align="center">
        <Text size="2" color="gray" style={{ minWidth: '30px' }}>
          Min
        </Text>
        <MonetaryValueInput
          value={rangeValue.min}
          onChange={(minValue) => onChange({ ...rangeValue, min: minValue })}
        />
      </Flex>
      <Flex direction="row" gap="2" align="center">
        <Text size="2" color="gray" style={{ minWidth: '30px' }}>
          Max
        </Text>
        <MonetaryValueInput
          value={rangeValue.max}
          onChange={(maxValue) => onChange({ ...rangeValue, max: maxValue })}
        />
      </Flex>
    </Flex>
  );
};

// Check if operation is a range operation
const isRangeOperation = (op: ConditionOperator): boolean =>
  op === ConditionOperator.Between || op === ConditionOperator.NotBetween;

const fingerprintValueComponentMap: Record<
  FingerprintValueType,
  React.ComponentType<any>
> = {
  [FingerprintValueType.MonetaryValue]: MonetaryValueInput,
  [FingerprintValueType.String]: TextFieldInput,
  [FingerprintValueType.Boolean]: TextFieldInput,
  [FingerprintValueType.Hash]: TextFieldInput,
  [FingerprintValueType.Null]: TextFieldInput,
  [FingerprintValueType.Number]: TextFieldInput,
};

export interface TransactionFingerprintEditorProps {
  initialFingerprint?: Partial<TransactionFingerprint>;
  onSave: (fingerprint: Partial<TransactionFingerprint>) => void;
  onCancel: () => void;
}

const TransactionFingerprintEditor = ({
  initialFingerprint,
  onSave,
  onCancel,
}: TransactionFingerprintEditorProps) => {
  const [selectedAttribute, setSelectedAttribute] = useState(
    initialFingerprint?.txField || transactionAttributes[0].key.toUpperCase(),
  );
  const [operation, setOperation] = useState<ConditionOperator>(
    initialFingerprint?.operation || ConditionOperator.Equals,
  );
  const [value, setValue] = useState<FingerprintValue | undefined>(
    initialFingerprint?.value,
  );

  // Get the valueType for the selected attribute
  const selectedAttr = transactionAttributes.find(
    (attr) => attr.key.toUpperCase() === selectedAttribute,
  );
  const expectedValueType =
    selectedAttr?.valueType || FingerprintValueType.String;
  const isNullable = selectedAttr?.nullable ?? false;

  // Get valid operations for this value type, filtering null ops if not nullable
  const validOperations = filterNullOperations(
    getOperationsForValueType(expectedValueType),
    isNullable,
  );

  // Helper to get initial value based on type and operation
  const getInitialValue = (
    valueType: FingerprintValueType,
    isRange: boolean,
  ) => {
    if (valueType === FingerprintValueType.MonetaryValue) {
      if (isRange) {
        return {
          min: { amount: '', currency: 'ETH' },
          max: { amount: '', currency: 'ETH' },
        };
      }
      return { amount: '', currency: 'ETH' };
    }
    if (isRange) {
      return { min: '', max: '' };
    }
    return '';
  };

  // Update value's valueType when selected attribute changes
  useEffect(() => {
    setValue((currentValue: FingerprintValue | undefined) => {
      if (!currentValue || currentValue.valueType !== expectedValueType) {
        return {
          value: getInitialValue(
            expectedValueType,
            isRangeOperation(operation),
          ),
          valueType: expectedValueType,
        };
      }
      return currentValue;
    });

    // Reset operation if current operation is not valid for the new value type
    setOperation((currentOperation: ConditionOperator) => {
      if (!validOperations.includes(currentOperation)) {
        return validOperations[0];
      }
      return currentOperation;
    });
  }, [selectedAttribute, expectedValueType, validOperations]);

  // Reset value when switching between range, list, and non-range operations
  useEffect(() => {
    setValue((currentValue: FingerprintValue | undefined) => {
      const needsRange = isRangeOperation(operation);
      const needsList = isListOperation(operation);
      const hasRange =
        currentValue?.value &&
        typeof currentValue.value === 'object' &&
        'min' in currentValue.value;
      // List values are stored as UUID strings
      const hasList =
        currentValue?.value && typeof currentValue.value === 'string';

      // If switching to list operation, clear to empty string (list ID)
      if (needsList && !hasList) {
        return {
          value: '',
          valueType: FingerprintValueType.String,
        };
      }

      // If switching from list to non-list, reset to appropriate type
      if (!needsList && hasList && !needsRange) {
        return {
          value: getInitialValue(expectedValueType, false),
          valueType: expectedValueType,
        };
      }

      if (needsRange !== hasRange && !needsList) {
        return {
          value: getInitialValue(expectedValueType, needsRange),
          valueType: expectedValueType,
        };
      }
      return currentValue;
    });
  }, [operation, expectedValueType]);

  const handleSaveClick = (e: MouseEvent<HTMLButtonElement>): void => {
    e.preventDefault();
    const fingerprint: Partial<TransactionFingerprint> = {
      txField: selectedAttribute,
      operation,
      value,
    };
    onSave(fingerprint);
  };

  const ValueFieldComponent = fingerprintValueComponentMap[expectedValueType];

  return (
    <Card size="1">
      <Flex direction="column" gap="2">
        <Select.Root
          value={selectedAttribute}
          onValueChange={(val) =>
            setSelectedAttribute(val as typeof selectedAttribute)
          }
        >
          <Select.Trigger />
          <Select.Content>
            {transactionAttributes.map((attr) => (
              <Select.Item key={attr.key} value={attr.key.toUpperCase()}>
                {attr.label}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>

        <Select.Root
          value={operation}
          onValueChange={(value) => setOperation(value as ConditionOperator)}
        >
          <Select.Trigger />
          <Select.Content>
            {validOperations.map((op: ConditionOperator) => (
              <Select.Item key={op} value={op}>
                {formatOperator(op)}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
        {isListOperation(operation) ? (
          // List operations use a ListSelector instead of a value input
          <ListSelector
            value={value?.value as string | undefined}
            onChange={(listId) => {
              setValue({
                value: listId,
                valueType: FingerprintValueType.String, // List ID is stored as string
              });
            }}
            // For address fields (FROM/TO), filter to ADDRESS lists only
            itemType={
              selectedAttr?.type === SlotDataType.Address
                ? ListItemType.Address
                : undefined
            }
            placeholder="Select a list (e.g., OFAC Sanctions)..."
          />
        ) : isRangeOperation(operation) ? (
          expectedValueType === FingerprintValueType.MonetaryValue ? (
            <MonetaryRangeInput
              value={value?.value}
              onChange={(newValue) => {
                setValue({
                  value: newValue,
                  valueType: expectedValueType,
                });
              }}
            />
          ) : (
            <NumberRangeInput
              value={value?.value}
              onChange={(newValue) => {
                setValue({
                  value: newValue,
                  valueType: expectedValueType,
                });
              }}
            />
          )
        ) : (
          <ValueFieldComponent
            value={value?.value}
            onChange={(newValue: FingerprintValue['value']) => {
              setValue({
                value: newValue,
                valueType: expectedValueType,
              });
            }}
          />
        )}
        <Flex gap="2" justify="between">
          <Button onClick={onCancel} variant="soft">
            Cancel
          </Button>
          <Button onClick={handleSaveClick}>
            {initialFingerprint ? 'Update Condition' : 'Save Condition'}
          </Button>
        </Flex>
      </Flex>
    </Card>
  );
};

export default TransactionFingerprintEditor;
